import React, { useEffect, useState, useRef } from "react";
import BreadCrumb from "../components/BreadCrumb";
import Meta from "../components/Meta";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Container from "../components/Container";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { loginUser, getUserCart, getuserProductWishlist, resetState } from "../features/user/userSlice";
import { BsEye, BsEyeSlash, BsEnvelope, BsLock, BsArrowRight, BsShieldCheck, BsCheckCircle } from "react-icons/bs";

// Enhanced validation schema with better security
let loginSchema = yup.object({
  email: yup
    .string()
    .required("Email is Required")
    .email("Email Should be valid")
    .max(100, "Email cannot exceed 100 characters")
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      "Please enter a valid email address"
    ),
  password: yup
    .string()
    .required("Password is Required")
    .min(8, "Password must be at least 8 characters")
    .max(128, "Password cannot exceed 128 characters")
    .matches(
      /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      "Password must contain at least one letter, one number and one special character"
    ),
});

const Login = () => {
  const authState = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);
  const [loginProcessed, setLoginProcessed] = useState(false);
  const [requiresEmailVerification, setRequiresEmailVerification] = useState(false);
  const [isDevelopmentMode] = useState(process.env.NODE_ENV === 'development');

  // Check if user came from guest order
  const fromGuestOrder = location.state?.fromGuestOrder;
  const guestOrderId = location.state?.orderId;

  useEffect(() => {
    dispatch(resetState());
    if (guestOrderId) {
      setOrderId(guestOrderId);
    }
    
    // Load reCAPTCHA script
    const loadRecaptchaScript = () => {
      if (!document.getElementById('recaptcha-script')) {
        const script = document.createElement('script');
        script.id = 'recaptcha-script';
        script.src = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`;
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);
      }
    };
    
    loadRecaptchaScript();
  }, [dispatch, guestOrderId]);

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: loginSchema,
    onSubmit: async (values) => {
      // Sanitize input
      const sanitizedValues = {
        email: values.email.trim().toLowerCase(),
        password: values.password
      };
      
      // Execute reCAPTCHA
      if (window.grecaptcha && process.env.REACT_APP_RECAPTCHA_SITE_KEY) {
        try {
          const token = await window.grecaptcha.execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY, { action: 'login' });
          setRecaptchaToken(token);
          sanitizedValues.recaptchaToken = token;
        } catch (error) {
          console.error('reCAPTCHA execution error:', error);
          // Still proceed with login even if reCAPTCHA fails (better UX)
        }
      }
      
      setIsLoading(true);
      dispatch(loginUser(sanitizedValues));
    },
  });

  // Destructure specific values from authState to avoid infinite loop
  const { user, isError, message } = authState || {};
  const userId = user?._id;

  useEffect(() => {
    // Only run this effect when user is successfully logged in and we haven't processed the login yet
    if (userId && !isError && !loginProcessed) {
      // Mark that we've processed the login to prevent infinite loop
      setLoginProcessed(true);
      
      const getTokenFromLocalStorage = localStorage.getItem("customer")
        ? JSON.parse(localStorage.getItem("customer"))
        : null;

      const config2 = {
        headers: {
          Authorization: `Bearer ${
            getTokenFromLocalStorage !== null ? getTokenFromLocalStorage.token : ""
          }`,
          Accept: "application/json",
        },
      };

      dispatch(getUserCart(config2));
      
      setTimeout(() => {
        dispatch(getuserProductWishlist());
      }, 100);
      
      setIsLoading(false);
      
      // Show success message for guest order transfer
      if (fromGuestOrder) {
        setShowSuccess(true);
        // Clear guest user ID from localStorage after successful login
        localStorage.removeItem("guestUserId");
        // Auto redirect to orders page after 3 seconds
        setTimeout(() => {
          navigate("/my-orders");
        }, 3000);
      } else {
        // Clear guest user ID from localStorage after successful login
        localStorage.removeItem("guestUserId");
        setTimeout(() => {
          navigate("/");
        }, 100);
      }
    }
    
    if (isError) {
      setIsLoading(false);
      // Reset loginProcessed when there's an error so user can try again
      setLoginProcessed(false);
      
      // Check if the error is related to email verification
      if (message?.response?.data?.message && 
          message.response.data.message.includes("Please verify your email address")) {
        // Only set requiresEmailVerification to true if not in development mode
        if (!isDevelopmentMode) {
          setRequiresEmailVerification(true);
        }
      }
    }
  }, [userId, isError, dispatch, navigate, fromGuestOrder, loginProcessed, message, isDevelopmentMode]);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-yellow-50 to-red-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-full mb-4 shadow-lg">
              <BsShieldCheck className="text-white text-3xl" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2 fredoka">Welcome To VEDA</h2>            <p className="text-gray-600">Enter your credentials to continue</p>
          </div>

          {/* Success Message for Guest Order */}
          {showSuccess && (
            <div className="mb-6 bg-green-50 border-2 border-green-200 rounded-2xl p-6 text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <BsCheckCircle className="text-green-500 text-3xl" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2 fredoka">Login Successful!</h3>
              <p className="text-gray-600 mb-4">
                Your guest order {orderId ? (authState?.orderedProduct?.order?.customOrderId || `#${orderId.slice(-8).toUpperCase()}`) : ''} has been transferred to your account.
              </p>
              <p className="text-gray-600">
                Redirecting to your orders page...
              </p>
            </div>
          )}

          {/* Card */}
          <div className="bg-white rounded-3xl shadow-xl p-8">
            {/* Error Alert */}
            {authState?.isError && !requiresEmailVerification && (
              <div className="mb-6 bg-red-50 border-2 border-red-200 rounded-2xl p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-red-800">
                      {authState?.message?.response?.data?.message || 
                       authState?.message?.message || 
                       authState?.message || 
                       "Invalid credentials. Please try again."}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Email Verification Required Alert */}
            {requiresEmailVerification && (
              <div className="mb-6 bg-yellow-50 border-2 border-yellow-200 rounded-2xl p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-semibold text-yellow-800 fredoka">Email Verification Required</h3>
                    <p className="text-sm text-yellow-700 mt-1">
                      Please verify your email address before logging in. Check your inbox for the verification email.
                    </p>
                    <div className="mt-3 flex space-x-3">
                      <Link 
                        to="/resend-verification" 
                        className="text-sm font-medium text-yellow-800 hover:text-yellow-900 underline"
                      >
                        Resend Verification Email
                      </Link>
                      <Link 
                        to="/signup" 
                        className="text-sm font-medium text-yellow-800 hover:text-yellow-900 underline"
                      >
                        Create New Account
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Development Mode Bypass Notice */}
            {isDevelopmentMode && (
              <div className="mb-6 bg-blue-50 border-2 border-blue-200 rounded-2xl p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-semibold text-blue-800 fredoka">Development Mode</h3>
                    <p className="text-sm text-blue-700">
                      Email verification is bypassed in development mode.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Guest Order Notice */}
            {fromGuestOrder && !showSuccess && (
              <div className="mb-6 bg-yellow-50 border-2 border-yellow-200 rounded-2xl p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <BsCheckCircle className="text-yellow-500 text-xl" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-semibold text-yellow-800 fredoka">Guest Order Detected</h3>
                    <p className="text-sm text-yellow-700">
                      Your order {orderId ? (authState?.orderedProduct?.order?.customOrderId || `#${orderId.slice(-8).toUpperCase()}`) : ''} will be automatically transferred to your account after login.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Form */}
            <form onSubmit={formik.handleSubmit} className="space-y-6">
              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2 fredoka">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <BsEnvelope className="text-red-500 text-lg" />
                  </div>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    placeholder="Enter your email"
                    value={formik.values.email}
                    onChange={formik.handleChange("email")}
                    onBlur={formik.handleBlur("email")}
                    className={`block w-full pl-12 pr-4 py-3 border-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                      formik.touched.email && formik.errors.email
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  />
                </div>
                {formik.touched.email && formik.errors.email && (
                  <p className="mt-2 text-sm text-red-600">{formik.errors.email}</p>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2 fredoka">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <BsLock className="text-red-500 text-lg" />
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    id="password"
                    placeholder="Enter your password"
                    value={formik.values.password}
                    onChange={formik.handleChange("password")}
                    onBlur={formik.handleBlur("password")}
                    className={`block w-full pl-12 pr-12 py-3 border-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                      formik.touched.password && formik.errors.password
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    {showPassword ? <BsEyeSlash className="text-xl" /> : <BsEye className="text-xl" />}
                  </button>
                </div>
                {formik.touched.password && formik.errors.password && (
                  <p className="mt-2 text-sm text-red-600">{formik.errors.password}</p>
                )}
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="rememberMe"
                    className="h-4 w-4 text-red-500 focus:ring-red-500 border-gray-300 rounded cursor-pointer"
                  />
                  <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-700 cursor-pointer fredoka">
                    Remember me
                  </label>
                </div>
                <div className="flex flex-col sm:flex-row items-center gap-3">
                  {requiresEmailVerification && (
                    <Link 
                      to="/resend-verification" 
                      className="text-sm font-semibold text-yellow-600 hover:text-yellow-700 transition-colors"
                    >
                      Resend Verification Email
                    </Link>
                  )}
                  <Link 
                    to="/forgot-password" 
                    className="text-sm font-semibold text-red-500 hover:text-red-600 transition-colors"
                  >
                    Forgot Password?
                  </Link>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading || showSuccess || (requiresEmailVerification && !isDevelopmentMode)}
                className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-red-500 to-red-600 text-white font-bold rounded-full shadow-lg hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-4 focus:ring-red-300 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Signing In...</span>
                  </>
                ) : showSuccess ? (
                  <>
                    <BsCheckCircle className="text-xl" />
                    <span>Redirecting...</span>
                  </>
                ) : requiresEmailVerification && !isDevelopmentMode ? (
                  <>
                    <BsEnvelope className="text-xl" />
                    <span>Email Verification Required</span>
                  </>
                ) : (
                  <>
                    <span>Sign In</span>
                    <BsArrowRight className="text-xl" />
                  </>
                )}
              </button>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500">or</span>
                </div>
              </div>

              {/* Google Sign-In Button */}
              <div className="mt-6">
                <a
                  href={`${process.env.REACT_APP_API_URL?.endsWith('/') ? process.env.REACT_APP_API_URL : process.env.REACT_APP_API_URL + '/'}user/auth/google`}
                  className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-300 rounded-full shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  <span>Sign in with Google</span>
                </a>
              </div>

              {/* Sign Up Link */}
              <div className="text-center">
                <p className="text-gray-600">
                  Don't have an account?{" "}
                  <Link 
                    to="/signup" 
                    state={{ fromGuestOrder, orderId: guestOrderId }}
                    className="font-bold text-red-500 hover:text-red-600 transition-colors"
                  >
                    Sign up here
                  </Link>
                </p>
              </div>
            </form>
          </div>

          {/* Trust Badges */}
          <div className="grid grid-cols-3 gap-4 mt-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-red-100 rounded-full mb-2">
                <BsShieldCheck className="text-red-500 text-2xl" />
              </div>
              <p className="text-xs text-gray-600 font-medium">Secure Login</p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-yellow-100 rounded-full mb-2">
                <BsLock className="text-yellow-600 text-2xl" />
              </div>
              <p className="text-xs text-gray-600 font-medium">Data Protected</p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-red-100 rounded-full mb-2">
                <BsEnvelope className="text-red-500 text-2xl" />
              </div>
              <p className="text-xs text-gray-600 font-medium">Email Verified</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;